import { apiFiles } from "@/api/file.api.ts";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { JobProfileSelect } from "@/components/select/JobProfileSelect.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Input,
  Select,
  Stack,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useRequestPositionSave } from "@/queries/recruit/use-hiring-plan.query.ts";
import { useDepartmentsTree } from "@/queries/use-departments.query.ts";
import { useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLocations } from "@/queries/use-locations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { ApiHiringPlanPayload, ApiHiringPlanRequest } from "@/types/recruit/hiring-plan.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";

const toOptionalNumber = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const num = typeof value === "string" ? Number(value.replace(/\s/g, "")) : value;
  return typeof num === "number" && Number.isNaN(num) ? undefined : num;
};

interface RequestPositionEditModalProps extends ModalProps {
  requestId: number;
  request?: ApiHiringPlanRequest;
}

export function RequestPositionEditModal({
  requestId,
  request,
  ...props
}: RequestPositionEditModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const divisions = useDivisionsSearchInline();
  const departments = useDepartmentsTree();
  const { data: locations } = useLocations();
  const [files, setFiles] = useState<ApiFile[]>(() =>
    request ? request.hiring_plan_data.files : []
  );
  const [syncedRequest, setSyncedRequest] = useState(request);
  if (request !== syncedRequest) {
    setSyncedRequest(request);
    if (request) setFiles(request.hiring_plan_data.files);
  }
  const { data: employeeMe } = useEmployeeMe();
  const { handleErrors } = useErrorHandle();
  const [additionalInfoExpanded, setAdditionalInfoExpanded] = useState<boolean>(
    !!(request?.hiring_plan_data?.requirements || request?.hiring_plan_data?.responsibilities)
  );
  const { close } = useModals();

  const { addRecent } = useRecent();

  const PositionRequestSchemaObject = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    description: z.string().nullable().optional(),
    responsibilities: z.string().nullable().optional(),
    requirements: z.string().nullable().optional(),
    division_id: z.number().nullable().optional(),
    department_id: z.number().nullable().optional(),
    job_profile_id: z.number().nullable().optional(),
    location_id: z.number().nullable().optional(),
    date_plan: z.date().nullable().optional(),
    currency_code: z.string().nullable().optional(),
    salary_min: z.preprocess(toOptionalNumber, z.number().nullable().optional()),
    salary_max: z.preprocess(toOptionalNumber, z.number().nullable().optional()),
  });

  const {
    handleSubmit,
    control,
    reset,
    register,
    formState: { errors, dirtyFields },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(PositionRequestSchemaObject),
  });
  const requestPositionSave = useRequestPositionSave(control);

  const onSubmit = handleSubmit(async (schema) => {
    const payload: ApiHiringPlanPayload = {
      title: schema.title,
      description: schema.description ?? null,
      requirements: schema.requirements ?? null,
      responsibilities: schema.responsibilities ?? null,
      department_id: schema.department_id ?? null,
      division_id: schema.division_id ?? null,
      salary_min: schema.salary_min ?? null,
      salary_max: schema.salary_max ?? null,
      currency_code: schema.currency_code ?? null,
      job_profile_id: schema.job_profile_id ?? null,
      location_id: schema.location_id ?? null,
      date_plan: schema.date_plan ?? null,
      file_uuids: files.map((file) => file.uuid),
    };

    requestPositionSave.mutate(
      { payload, id: requestId },
      {
        onSuccess: (res) => {
          if (dirtyFields.currency_code && schema.currency_code) {
            addRecent([{ type: ApiRecentKey.currency, values: [schema.currency_code] }]);
          }
          if (requestId) {
            analyticsService.trackEvent(analyticEvents.hiringPlan.requestCreated, {
              [analyticProperties.id]: res.approval_request_details?.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.hiringPlan.requestEdited, {
              [analyticProperties.id]: res.approval_request_details?.id,
            });
          }

          close(id);
        },
      }
    );
  });

  useEffect(() => {
    if (!request) return;

    reset({
      title: request.hiring_plan_data.title,
      description: request.hiring_plan_data.description,
      requirements: request.hiring_plan_data.requirements,
      responsibilities: request.hiring_plan_data.responsibilities,
      department_id: request.hiring_plan_data.department?.is_archived
        ? null
        : (request.hiring_plan_data.department?.id ?? null),
      division_id: request.hiring_plan_data.division?.is_archived
        ? null
        : (request.hiring_plan_data.division?.id ?? null),
      salary_min: request.hiring_plan_data.salary_min
        ? parseFloat(request.hiring_plan_data.salary_min)
        : null,
      salary_max: request.hiring_plan_data.salary_max
        ? parseFloat(request.hiring_plan_data.salary_max)
        : null,
      currency_code: request.hiring_plan_data.currency?.code ?? null,
      job_profile_id: request.hiring_plan_data.job_profile?.is_archived
        ? null
        : (request.hiring_plan_data.job_profile?.id ?? null),
      location_id: request.hiring_plan_data.location?.is_archived
        ? null
        : (request.hiring_plan_data.location?.id ?? null),
      date_plan: request.hiring_plan_data.date_plan ?? null,
    });
  }, [request]);

  async function removeFile(file: ApiFile) {
    setFiles(files.filter((f) => f.id !== file.id));
    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  return (
    <Modal
      {...props}
      title={
        !requestId
          ? t("hiring_plan:creation_request_hiring_plan")
          : t("hiring_plan:editing_request_hiring_plan")
      }
      withCloser={true}
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            onClick={onSubmit}
            size={"lg"}
            isLoading={requestPositionSave.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        <Input
          type={"text"}
          autoFocus={true}
          {...register("title")}
          label={t("common:title")}
          required={true}
          error={errors.title?.message}
        />
        <Controller
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("core:division")}
              emptyTitle={t("common:select")}
              value={value}
              onChange={onChange}
              searchable
              isClearable={true}
              loadOptionsOnOpen
              onSearchOptions={(text) => divisions.search(text)}
              searchOptions={divisions.options}
              error={errors.division_id?.message}
            />
          )}
          control={control}
          name={"division_id"}
        />
        <Controller
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("core:department")}
              emptyTitle={t("common:select")}
              value={value}
              onChange={onChange}
              searchable
              isClearable={true}
              options={departments.data}
              error={errors.department_id?.message}
            />
          )}
          control={control}
          name={"department_id"}
        />
        <Controller
          control={control}
          name={"job_profile_id"}
          render={({ field: { value, onChange } }) => (
            <JobProfileSelect
              label={t("core:job_profile")}
              isClearable={true}
              value={value}
              onChange={onChange}
              error={errors.job_profile_id?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="location_id"
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("common:location")}
              options={locations || []}
              emptyTitle={t("common:select")}
              value={value}
              onChange={onChange}
              searchable
              isClearable={true}
              error={errors.location_id?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="date_plan"
          render={({ field: { value, onChange } }) => (
            <Datepicker
              value={value}
              onChange={onChange}
              label={t("hiring_plan:plan_date")}
              isClearable={true}
              error={errors.date_plan?.message}
            />
          )}
        />
        <Title header={t("common:budget")} paddingTop={true} paddingBottom={true} size={"xs"} />
        <Controller
          control={control}
          name={`currency_code`}
          render={({ field: { value, onChange } }) => (
            <CurrencySelect
              label={t("common:currency")}
              emptyTitle={t("common:select")}
              isClearable={true}
              value={value}
              searchable
              onChange={onChange}
              error={errors.currency_code?.message}
            />
          )}
        />
        <Input
          label={t("ats:min_salary")}
          type={"text"}
          {...register("salary_min")}
          error={errors.salary_min?.message}
        />
        <Input
          label={t("ats:max_salary")}
          type={"text"}
          {...register("salary_max")}
          error={errors.salary_max?.message}
        />
        <Title
          header={t("common:description")}
          paddingTop={true}
          paddingBottom={true}
          size={"xs"}
        />
        <Stack gap={"md"}>
          <Textarea label={t("common:add_description")} {...register("description")} />
          {additionalInfoExpanded ? (
            <Stack>
              <Title header={t("hiring_plan:responsibilities")} size={"xs"} paddingBottom />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value || ""}
                    onChange={onChange}
                    placeholder={t("hiring_plan:enter_responsibilities")}
                    error={errors.responsibilities?.message}
                  />
                )}
                control={control}
                name={"responsibilities"}
              />
              <Title header={t("hiring_plan:requirements")} size={"xs"} paddingBottom paddingTop />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value || ""}
                    onChange={onChange}
                    placeholder={t("hiring_plan:enter_requirements")}
                    error={errors.requirements?.message}
                  />
                )}
                control={control}
                name={"requirements"}
              />
            </Stack>
          ) : (
            <Stack direction={"horizontal"}>
              <Button onClick={() => setAdditionalInfoExpanded(true)} variant={"secondary"}>
                {t("hiring_plan:add_additional_information")}
              </Button>
            </Stack>
          )}

          {files && files.length > 0 && (
            <Stack gap={"sm"}>
              {files.map((file) => (
                <FileItem key={`file${file.id}`} file={file} onRemove={() => removeFile(file)} />
              ))}
            </Stack>
          )}

          <Stack items={"start"}>
            <Box>
              <FileUpload
                entityId={employeeMe?.id}
                entityType={ApiEntityTypes.employee}
                afterSend={(file) => {
                  if (file) setFiles([...files, file]);
                }}
                buttonText={t("common:attach")}
              />
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
}
